import React from 'react';
import {
  formatCurrency,
  getPaymentTypeIcon,
  getPaymentTypeColor,
  getOperationTypeBadge
} from './kasaBankHelpers';
import { DateRangeFilter, Pagination } from './sharedControls';

/**
 * "Wszystkie operacje" tab content - filters, selection, table, export buttons.
 */
function AllOperationsTab({
  filteredOperations,
  selectedOperations,
  selectAll,
  paymentFilters,
  dateFilters,
  setDateFilters,
  togglePaymentFilter,
  toggleAllFilters,
  toggleOperationSelection,
  toggleSelectAll,
  exportToCSV,
  exportToPDF,
  canEditOperations,
  onEditOperation,
  page = 1,
  pageLimit = 50,
  totalCount = 0,
  operacjeLoading = false,
  onPageChange,
  onLimitChange
}) {
  const selectionCount = selectedOperations.size;

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h5 style={{ margin: 0, fontWeight: '600', color: 'var(--text)' }}>
          <i className="fas fa-list-ul" style={{ color: 'var(--text-muted)', marginRight: '0.5rem' }}></i>
          Wszystkie operacje ({totalCount} {operacjeLoading ? '· ladowanie...' : ''})
          {selectionCount > 0 && (
            <span style={{
              marginLeft: '0.5rem', padding: '0.25rem 0.5rem',
              border: '1px solid var(--info)', color: 'var(--info)' ,
              borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600'
            }}>
              {selectionCount} wybranych
            </span>
          )}
        </h5>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <ExportButton
            label="Eksport CSV"
            icon="fas fa-file-csv"
            color="var(--success)"
            disabled={selectionCount === 0}
            onClick={exportToCSV}
          />
          <ExportButton
            label="Eksport PDF"
            icon="fas fa-file-pdf"
            color="var(--danger)"
            disabled={selectionCount === 0}
            onClick={exportToPDF}
          />
          <FilterToggleButton label="Zaznacz wszystkie" color="var(--success)" onClick={() => toggleAllFilters(true)} />
          <FilterToggleButton label="Odznacz wszystkie" color="var(--danger)" onClick={() => toggleAllFilters(false)} />
        </div>
      </div>

      {/* Date filters */}
      <DateRangeFilter dateFilters={dateFilters} setDateFilters={setDateFilters} />

      {/* Payment type filters */}
      <PaymentFilterBar paymentFilters={paymentFilters} togglePaymentFilter={togglePaymentFilter} />

      {/* Operations table */}
      {filteredOperations.length > 0 ? (
        <OperationsTable
          operations={filteredOperations}
          selectedOperations={selectedOperations}
          selectAll={selectAll}
          toggleSelectAll={toggleSelectAll}
          toggleOperationSelection={toggleOperationSelection}
          canEditOperations={canEditOperations}
          onEditOperation={onEditOperation}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          <i className="fas fa-filter fa-3x" style={{ marginBottom: '0.6rem', opacity: 0.3 }}></i>
          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500' }}>
            {Object.values(paymentFilters).every((v) => !v)
              ? 'Zaznacz co najmniej jeden typ płatności'
              : 'Brak operacji pasujących do wybranych filtrów'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && onPageChange && (
        <Pagination
          page={page}
          pageLimit={pageLimit}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        />
      )}
    </div>
  );
}

/* --------- sub-components (file-private) --------- */

function ExportButton({ label, icon, color, disabled, onClick }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px', fontSize: '12px',
        border: `1px solid ${disabled ? 'var(--border)' : color}`,
        backgroundColor: disabled ? 'var(--bg)' : color,
        color: disabled ? 'var(--text-muted)' : '#fff' ,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.25rem',
        opacity: disabled ? 0.6 : 1, transition: 'all 0.2s ease'
      }}
    >
      <i className={icon}></i>
      {label}
    </button>
  );
}

function FilterToggleButton({ label, color, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px', fontSize: '12px',
        border: `1px solid ${color}`,
        backgroundColor: 'var(--bg)', color, cursor: 'pointer', fontWeight: '500'
      }}
    >
      {label}
    </button>
  );
}

function PaymentFilterBar({ paymentFilters, togglePaymentFilter }) {
  return (
    <div style={{
      marginBottom: '1.5rem', padding: '1rem',
      backgroundColor: 'var(--bg)', borderRadius: '0.5rem', border: '1px solid var(--border)'
    }}>
      <div style={{ marginBottom: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        <i className="fas fa-filter" style={{ marginRight: '0.5rem' }}></i>
        Filtruj według typu płatności:
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
        {Object.entries(paymentFilters).map(([paymentType, isChecked]) => (
          <label
            key={paymentType}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer',
              padding: '0.5rem', borderRadius: '0.25rem',
              backgroundColor: isChecked ? getPaymentTypeColor(paymentType) + '15' : 'var(--card-bg)' ,
              border: `1px solid ${isChecked ? getPaymentTypeColor(paymentType) : 'var(--border)'}`,
              transition: 'all 0.15s ease-in-out'
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => togglePaymentFilter(paymentType)}
              style={{ accentColor: getPaymentTypeColor(paymentType), cursor: 'pointer' }}
            />
            <i className={getPaymentTypeIcon(paymentType)} style={{ color: getPaymentTypeColor(paymentType), fontSize: '0.875rem' }}></i>
            <span style={{
              textTransform: 'capitalize', fontSize: '0.875rem',
              fontWeight: isChecked ? '600' : '400',
              color: isChecked ? getPaymentTypeColor(paymentType) : 'var(--text-muted)'
            }}>
              {paymentType}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function OperationsTable({
  operations, selectedOperations, selectAll,
  toggleSelectAll, toggleOperationSelection,
  canEditOperations, onEditOperation
}) {
  const thStyle = {
    padding: '0.3rem 0.2rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '0.65rem'
  };

  return (
    <div style={{ overflowX: 'auto', maxHeight: '350px', overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', minWidth: '650px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)' }}>
            <th style={{ ...thStyle, textAlign: 'center', width: '40px' }}>
              <input type="checkbox" checked={selectAll} onChange={toggleSelectAll} style={{ cursor: 'pointer', transform: 'scale(1.0)' }} />
            </th>
            {['Data', 'Typ', 'Płatność', 'Nr dok.', 'Kontrahent', 'Opis'].map((h) => (
              <th key={h} style={thStyle}>{h}</th>
            ))}
            <th style={{ ...thStyle, textAlign: 'right' }}>Kwota</th>
            <th style={{ ...thStyle, textAlign: 'center', width: '80px' }}>Akcje</th>
          </tr>
        </thead>
        <tbody>
          {operations.map((operacja, index) => {
            const badge = getOperationTypeBadge(operacja.typ_operacji);
            return (
              <tr key={operacja.id || index} style={{ borderBottom: '1px solid var(--bg)' }}>
                <td style={{ padding: '0.35rem 0.25rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedOperations.has(operacja.id || index)}
                    onChange={() => toggleOperationSelection(operacja.id || index)}
                    style={{ cursor: 'pointer', transform: 'scale(1.1)' }}
                  />
                </td>
                <td style={{ padding: '0.35rem 0.25rem' }}>{operacja.data_operacji}</td>
                <td style={{ padding: '0.35rem 0.25rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
                    fontSize: '0.75rem', fontWeight: '600',
                    color: badge.color, backgroundColor: badge.bg
                  }}>
                    {badge.text}
                  </span>
                </td>
                <td style={{ padding: '0.35rem 0.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <i className={getPaymentTypeIcon(operacja.typ_platnosci)} style={{ color: getPaymentTypeColor(operacja.typ_platnosci) }}></i>
                    <span style={{ textTransform: 'capitalize' }}>{operacja.typ_platnosci}</span>
                  </div>
                </td>
                <td style={{ padding: '0.35rem 0.25rem' }}>
                  <span style={{
                    fontFamily: 'monospace', fontSize: '0.8rem',
                    backgroundColor: 'var(--bg)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem'
                  }}>
                    {operacja.numer_dokumentu || '-'}
                  </span>
                </td>
                <td style={{ padding: '0.35rem 0.25rem' }}>{operacja.kontrahent || '-'}</td>
                <td style={{ padding: '0.35rem 0.25rem' }}>{operacja.opis || '-'}</td>
                <td style={{
                  padding: '0.35rem 0.25rem', textAlign: 'right', fontWeight: '600',
                  color: operacja.typ_operacji === 'KP' ? 'var(--success)' : 'var(--danger)'
                }}>
                  {operacja.typ_operacji === 'KP' ? '+' : '-'}{formatCurrency(operacja.kwota)}
                </td>
                {/* Przycisk zawsze widoczny; bez uprawnienia kasa_bank.edycja - nieaktywny z podpowiedzia
                    (wlasciciel 2026-09-11: pracownicy nie edytuja operacji, chyba ze nadane w uprawnieniach) */}
                <td style={{ padding: '0.35rem 0.25rem', textAlign: 'center' }}>
                  <button
                    onClick={() => canEditOperations && onEditOperation(operacja)}
                    disabled={!canEditOperations}
                    aria-disabled={!canEditOperations}
                    data-testid={`btn-edytuj-${operacja.id}`}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
                      color: canEditOperations ? 'var(--text)' : 'var(--text-muted)',
                      fontSize: '11px', cursor: canEditOperations ? 'pointer' : 'not-allowed',
                      opacity: canEditOperations ? 1 : 0.55,
                      display: 'flex', alignItems: 'center', gap: '0.25rem',
                      boxShadow: canEditOperations ? '0 1px 3px rgba(13, 110, 253, 0.2)' : 'none', transition: 'all 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      if (!canEditOperations) return;
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = '0 2px 6px rgba(13, 110, 253, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      if (!canEditOperations) return;
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 1px 3px rgba(13, 110, 253, 0.2)';
                    }}
                    title={canEditOperations ? 'Edytuj operację' : 'Brak uprawnienia do edycji operacji (Uprawnienia → Kasa/Bank → edycja)'}
                  >
                    <i className="fas fa-edit"></i>
                    Edytuj
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AllOperationsTab;
